import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { useBlogPosts, formatDate } from "@/hooks/useBlogPosts";

const BlogSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const { posts: allPosts } = useBlogPosts(isInView);
  const posts = allPosts.slice(0, 3);

  return (
    <section id="nyheter" ref={ref} className="py-24 md:py-36 bg-envir-charcoal overflow-hidden">
      <div className="section-padding max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="flex items-end justify-between mb-16"
        >
          <div>
            <span className="text-sm font-body font-500 tracking-widest uppercase text-envir-green-bright mb-4 block">
              Aktuelt
            </span>
            <h2 className="heading-lg text-primary-foreground">Nyheter og innsikt</h2>
          </div>
          <Link
            to="/nyheter"
            className="hidden md:inline-flex items-center gap-1.5 text-sm font-display font-500 text-envir-green-bright hover:gap-2.5 transition-all duration-300"
          >
            Se alle <ArrowUpRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {posts.map((post, i) => (
            <motion.div
              key={post.slug}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
            >
              <Link to={`/blogg/${post.slug}`} className="group block h-full">
                <div className="overflow-hidden rounded-xl mb-4 aspect-[16/10]">
                  <img
                    src={post.thumb || post.image}
                    width={640}
                    height={400}
                    decoding="async"
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-display font-500 tracking-wider uppercase text-envir-green-bright bg-envir-green-bright/10 px-2.5 py-0.5 rounded-full">
                    {post.tag}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-body text-primary-foreground/70">
                    <Calendar className="w-3 h-3" aria-hidden="true" />
                    {formatDate(post.date)}
                  </span>
                </div>
                <h3 className="font-display font-500 text-primary-foreground text-base md:text-lg leading-snug mb-2 group-hover:text-envir-green-bright transition-colors duration-300 line-clamp-2">
                  {post.title}
                </h3>
                <span className="inline-flex items-center gap-1.5 text-sm font-display font-500 text-envir-green-bright group-hover:gap-2.5 transition-all duration-300">
                  Les mer <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        <Link
          to="/nyheter"
          className="md:hidden inline-flex items-center gap-1.5 text-sm font-display font-500 text-envir-green-bright hover:gap-2.5 transition-all duration-300 mt-10"
        >
          Se alle artikler <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export default BlogSection;
